import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormHelperText } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import strings from 'strings';
import CustomDatePicker from 'UI/components/atoms/CustomDatePicker';
import { TimeSelect } from 'UI/components/molecules/TimeSelect';

import { INTERVIEW_TYPE, INTERVIEW_TYPES } from './ScheduleCalls.constants';
import { useStyles } from './ScheduleCalls.styles';

const VALIDATION_MESSAGES = strings.sendouts.interviews.scheduleCalls.validations;

const FIELD_PRFIXES = {
  InterviewDate: 'InterviewDate',
  InterviewTime: 'InterviewTime',
  DateValidation: 'DateValidation'
};

const FormFieldsMap = Object.fromEntries(
  INTERVIEW_TYPES.flatMap(type => [
    [
      `${type}${FIELD_PRFIXES.InterviewDate}`,
      { key: `${type.toLowerCase()}${FIELD_PRFIXES.InterviewDate}` }
    ],
    [
      `${type}${FIELD_PRFIXES.InterviewTime}`,
      { key: `${type.toLowerCase()}${FIELD_PRFIXES.InterviewTime}` }
    ]
  ])
);

export const ScheduleCalls = ({ disabled = false }) => {
  const classes = useStyles();
  const { errors, register, unregister, watch, setValue, getValues, triggerValidation } =
    useFormContext();

  const formValues = watch();

  useEffect(() => {
    INTERVIEW_TYPES.forEach(type => {
      register(FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewDate}`].key, {
        validate(date) {
          const time = getValues()[FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewTime}`].key];
          const incompleteSelection = (date && !time) || (time && !date);
          return !incompleteSelection || VALIDATION_MESSAGES.incompleteCall;
        }
      });
      register(FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewTime}`].key);
      register(FIELD_PRFIXES.DateValidation, {
        validate() {
          const {
            hiringauthorityInterviewDate,
            hiringauthorityInterviewTime,
            candidateInterviewDate,
            candidateInterviewTime
          } = getValues();

          const atLeastOneTimestamp =
            (hiringauthorityInterviewDate && hiringauthorityInterviewTime) ||
            (candidateInterviewDate && candidateInterviewTime);

          return Boolean(atLeastOneTimestamp) || VALIDATION_MESSAGES.atLeastOneCall;
        }
      });
    });

    return () => {
      INTERVIEW_TYPES.forEach(type => {
        unregister(FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewDate}`].key);
        unregister(FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewTime}`].key);
      });
    };
  }, [register, unregister, getValues]);

  const handleDateChange = (name, value) => {
    setValue(name, value ? moment(value) : null, { shouldValidate: true });
    triggerValidation();
  };

  const handleTimeChange = name => value => {
    setValue(name, value, { shouldValidate: true });
    triggerValidation();
  };

  return (
    <div>
      <Typography variant="subtitle1">Schedule Calls</Typography>
      <Grid container className={classes.fieldsContainer}>
        {INTERVIEW_TYPES.map(type => {
          const dateKey = FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewDate}`].key;
          const timeKey = FormFieldsMap[`${type}${FIELD_PRFIXES.InterviewTime}`].key;

          return (
            <div key={type} className={classes.fieldsSubcontainer}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  {type === INTERVIEW_TYPE.HiringAuthority
                    ? strings.shared.fields.hiringAuthority
                    : type}
                </Typography>
              </Grid>
              <Grid container item spacing={2}>
                <Grid item xs={6}>
                  <CustomDatePicker
                    disablePast
                    disabled={disabled}
                    isClearable
                    error={!!errors[dateKey]}
                    helperText={errors[dateKey]?.message}
                    label="Date *"
                    name={dateKey}
                    value={formValues[dateKey] || null}
                    onDateChange={handleDateChange}
                    placeholder="Date *"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TimeSelect
                    disabled={disabled}
                    error={!!errors[timeKey]}
                    errorText={errors[timeKey]?.message}
                    name={timeKey}
                    placeholder={strings.shared.fields.timeRequired}
                    value={formValues[timeKey] || null}
                    onChange={handleTimeChange(timeKey)}
                  />
                </Grid>
              </Grid>
            </div>
          );
        })}
        <Grid item xs={12}>
          {errors[FIELD_PRFIXES.DateValidation]?.message && (
            <FormHelperText error>{errors[FIELD_PRFIXES.DateValidation]?.message}</FormHelperText>
          )}
        </Grid>
      </Grid>
    </div>
  );
};
